import {
  isEvidenceGap,
  type WorkspaceSource,
  type WorkspaceTask,
} from '../../../../hooks/advisory/advisoryWorkspace';
import type { AdvisoryWorkspaceStatus } from './advisoryWorkspaceTypes';

// Presentation only: elapsed time never changes server readiness or makes a gap waivable.
export const LONG_RESEARCH_MS = 15 * 60 * 1000;
const INTERRUPTIONS = new Set([
  'connectionInterrupted',
  'executionInterrupted',
  'workerInterrupted',
  'compressionInterrupted',
]);
export function backgroundHint(
  task?: WorkspaceTask,
  sourceError?: string | null
): string | undefined {
  if (task && ['completed', 'failed', 'needsInput', 'cancelled'].includes(task.status))
    return undefined;
  const error = task?.errorCode ?? sourceError;
  if (error === 'dispatchUncertain' || error === 'dispatchRejected')
    return 'advisory:workspace.live.background.' + error;
  if (error && INTERRUPTIONS.has(error)) return 'advisory:workspace.live.background.interrupted';
  return undefined;
}
export function taskStatus(task?: WorkspaceTask): AdvisoryWorkspaceStatus {
  if (!task) return 'waiting';
  if (task.status === 'completed') return task.phase === 'partial' ? 'partial' : 'completed';
  if (['failed', 'needsInput', 'cancelled'].includes(task.status)) return 'partial';
  if (task.phase === 'submissionUncertain' || backgroundHint(task)) return 'slow';
  return 'running';
}
export function sourceProgress(
  source: WorkspaceSource,
  tasks: Record<string, WorkspaceTask>,
  nowMs: number
): { status: AdvisoryWorkspaceStatus; hintKey?: string; statusKey: string } {
  const candidate = tasks[source.sourceId];
  const task = candidate?.generation === source.generation ? candidate : undefined;
  const result = {
    status: 'running' as AdvisoryWorkspaceStatus,
    statusKey: 'advisory:workspace.live.sourceStatus.' + source.status,
    hintKey: undefined as string | undefined,
  };
  if (source.status === 'ready') return { ...result, status: 'ready' };
  if (isEvidenceGap(source.status)) {
    if (source.kind === 'profile')
      return {
        ...result,
        status: 'needsInput',
        hintKey: 'advisory:workspace.live.companyHint.' + source.status,
      };
    let statusKey = result.statusKey;
    if (source.status === 'failed')
      statusKey =
        'advisory:workspace.live.sourceStatus.' +
        (source.kind === 'url'
          ? source.errorCode === 'sourceUnavailable'
            ? 'sourceUnavailable'
            : 'pageFailed'
          : 'fileFailed');
    else if (source.status === 'ingestionFailed')
      statusKey =
        'advisory:workspace.live.sourceStatus.' +
        (source.kind === 'url' ? 'pageIngestionFailed' : 'fileIngestionFailed');
    return {
      ...result,
      status: 'needsInput',
      statusKey,
    };
  }
  if (source.status === 'awaitingUpload') return { ...result, status: 'needsInput' };
  if (source.status === 'submissionUncertain')
    return {
      ...result,
      status: 'slow',
      hintKey: 'advisory:workspace.live.companyHint.submissionUncertain',
    };
  const hint = backgroundHint(task, source.errorCode);
  if (hint) return { status: 'slow', hintKey: hint, statusKey: hint };
  const startedAt = task?.startedAt ? Date.parse(task.startedAt) : NaN;
  if (
    source.kind === 'profile' &&
    source.status === 'researching' &&
    task?.status === 'running' &&
    Number.isFinite(startedAt) &&
    nowMs - startedAt >= LONG_RESEARCH_MS
  )
    return {
      status: 'slow',
      hintKey: 'advisory:workspace.live.background.slowResearch',
      statusKey: 'advisory:workspace.live.background.slowResearch',
    };
  return result;
}
